import { Grid, Heading, InputOnFocus, Button } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import FormBuilder from '@afterpaytouch/form'
import { Channel, IdentityErrorCode, IdentityDocumentVerificationStatus, IdentityMedicareColour } from '@afterpaytouch/portal-api/identity'
import { getIdentityVerifyErrorCode } from '@afterpaytouch/portal-api/identity/utils'
import { FormWrapper } from '..'
import { ErrorMessage } from '../VerifyIdentity/components'
import { DocumentProps } from '../../../model'
import { useConsumerSelector, useIdentityMedicareMutation, useAppRouter, useIsPartialVerified } from '../../../state'
import { formatNumberForBackend, getMedicareSchema, MedicareForm } from './utils'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { isDifferentWithProfileDOB, isDifferentWithProfileNames, IsRetryPageContext } from '../VerifyIdentity/helper'
import { compareObjects } from '../../../utils/comparison'
import { USER_VERIFICATION_ERRORS } from '../VerifyIdentity/constants'
import { useConsumerSizes } from '../../../utils/responsive'
import { SupportedLocale } from '@afterpay/types'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'
import { Route } from '../../../config/router'

const I18N_NAMESPACE = ['common', 'verifyIdentity']

interface SchemaUpdates {
  selectedColour?: IdentityMedicareColour
  showProfileNameAlert: boolean
  showProfileDobAlert: boolean
}

const terminalErrors = new Set<IdentityErrorCode>([IdentityErrorCode.MaxAttemptsReached, IdentityErrorCode.MaxAttemptsReachedForType])

export const Medicare: FunctionComponent<DocumentProps> = ({ onSuccess = () => {}, onFailure = () => {} }) => {
  const amplitude = useAmplitudeWithEnduringEventProperties()
  const [lock, setLock] = useState(false)
  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    showProfileNameAlert: false,
    showProfileDobAlert: false,
  })
  // @ts-ignore: OPERATION BLEED STOPPER
  const [error, setError] = useState<IdentityErrorCode>(null)
  const { t } = useTranslation(I18N_NAMESPACE)
  const { givenNames, otherGivenNames, surname, dateOfBirth } = useConsumerSelector()
  const { headingSize, buttonSize } = useConsumerSizes()
  const router = useRouter()
  const appRouter = useAppRouter()
  const [showVerifyLaterModal, setShowVerifyLaterModal] = useState(false)
  const [identityVerifyMedicare] = useIdentityMedicareMutation()
  const [submittedRequest, setSubmittedRequest] = useState(undefined)
  const isPartialVerified = useIsPartialVerified()
  const isRetryPage = useContext(IsRetryPageContext)
  const verifyButtonAnalytics =
    isPartialVerified && isRetryPage ? TrackingEvent.PRESSED_HARDID_RETRY_PAGE_VERIFY_BUTTON : TrackingEvent.PRESSED_HARDID_MEDICARE_PAGE_VERIFY_BUTTON
  useEffect(() => {
    if (!terminalErrors.has(error)) {
      // Scroll to top
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [error])

  const profileDateOfBirth = dateOfBirth ?? ''

  const handleVerifyLaterClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }

  const handleModalClose = (): void => {
    setShowVerifyLaterModal(false)
  }

  const handleVerifyNowClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }

  const handleSkipClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    appRouter.push(Route.HOME)
  }

  const handleSubmit = async (data: MedicareForm): Promise<void> => {
    // @ts-ignore: OPERATION BLEED STOPPER
    setError(null)
    try {
      setLock(true)
      amplitude.logEvent(verifyButtonAnalytics)
      const request = {
        ...data,
        channel: Channel.PORTAL,
        number: formatNumberForBackend(data.number),
      }

      if (compareObjects(request, submittedRequest)) {
        setLock(false)
        setError(IdentityErrorCode.IdentityAlreadyRejected)
        return
      }

      const response = await identityVerifyMedicare(request).unwrap()

      if (response.status === IdentityDocumentVerificationStatus.VERIFIED) {
        onSuccess()
      } else if (response.status === IdentityDocumentVerificationStatus.REJECTED) {
        setLock(false)
        // @ts-ignore: OPERATION BLEED STOPPER
        setSubmittedRequest(request)
        setError(IdentityErrorCode.IdentityCheckRejected)
        onFailure?.()
      }
    } catch (e) {
      if ([ErrorCode.MaxAttemptsReached, ErrorCode.MaxAttemptsReachedForType].includes(e?.data?.errorCode)) {
        router.push(`${Route.TERMINAL_ERROR}/identityVerificationFailedManual`)
        return
      }

      // Handle regular errors with logging
      const error = getIdentityVerifyErrorCode(e)
      amplitude.logEvent(TrackingEvent.IDENTITY_VERIFICATION_ERROR, {
        component: 'Medicare',
        errorType: e?.status ?? 'unknown',
        errorCode: e?.data?.errorCode ?? 'unknown',
        resolvedError: error ?? 'default',
      })

      setError(error ?? IdentityErrorCode.Default)
      setLock(false)
    }
  }

  const onNameBlur = (ev: InputOnFocus): void => {
    setSchemaUpdates({
      ...schemaUpdates,
      showProfileNameAlert: isDifferentWithProfileNames(ev.target.value, { givenNames, otherGivenNames, surname }),
    })
  }

  const handleOnChange = (data: MedicareForm): void => {
    // @ts-ignore: OPERATION BLEED STOPPER
    setError(null)
    setSchemaUpdates({
      ...schemaUpdates,
      selectedColour: data.colour,
      showProfileDobAlert: isDifferentWithProfileDOB(data.dateOfBirth, profileDateOfBirth),
    })
  }

  const components = {
    footer: () => {
      return (
        <>
          <Grid>
            <Button.Ghost testNameSpace='verify-medicare-verify-later' disabled={lock} onClick={handleVerifyLaterClick} padding='Fluid'>
              {t('verifyIdentity:verifyLater:buttonText')}
            </Button.Ghost>
          </Grid>
          <Grid>
            <Button.Primary loading={lock} type='submit' disabled={lock} padding='Fluid'>
              {t('verifyIdentity:medicare:cta')}
            </Button.Primary>
          </Grid>
        </>
      )
    },
  }

  return (
    <>
      <Grid>
        <Heading size={headingSize}>{t('verifyIdentity:document:medicare')}</Heading>
      </Grid>
      <ErrorMessage identityType='medicare' error={error} testNameSpace={'verify-identity-medicare-error'} />
      <FormWrapper>
        <FormBuilder
          disabled={lock}
          schema={getMedicareSchema({
            t,
            buttonSize,
            submitted: lock,
            profileDateOfBirth,
            onNameBlur,
            locale: router.locale as SupportedLocale,
            error: USER_VERIFICATION_ERRORS.includes(error),
            components,
            ...schemaUpdates,
          })}
          testNameSpace={'verify-identity-medicare'}
          onSubmit={handleSubmit}
          onChange={handleOnChange}
        />
      </FormWrapper>

      <VerifyLaterModal
        show={showVerifyLaterModal}
        onClose={handleModalClose}
        onVerifyNow={handleVerifyNowClick}
        onSkip={handleSkipClick}
        testNameSpace='verify-later'
      />
    </>
  )
}
